import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    BottomDwellerContainer,
    ContentContainer,
    GrabMe,
    GrabMeContainer,
    ANIM_DURATION,
    Backdrop,
    Overlay,
} from "./style";
import useBodyScrollLock from "../../Helpers/hooks/useBodyScrollLock";

const USE_DWELLER_SHIFT = false;
const USE_DWELLER_FADE = true;
const DWELLER_FADE_MODIFIER = 2;
const DWELLER_SHIFT_MODIFIER = 1;

let touchStart = 0;
const FULL_ANIM_STEPS = 20;
let delta = 0;

let lastEventListener = 0;

const BottomDweller = (props) => {
    const [expanded, setExpanded] = useState(props.expanded);
    const [animationRunning, setAnimationRunning] = useState(false);

    const setNewDwellerHeight = () => {
        const dweller = document.getElementById("bottom-dweller");
        if (!dweller) return;

        const getHeight = () => {
            if (expanded) {
                return `${window.innerHeight * 0.9 + Math.min(0, delta)}px`;
            } else {
                return `${props.bottomOffset + Math.max(0, delta)}px`;
            }
        };

        dweller.style.height = getHeight();
        dweller.style.removeProperty("transition");
    };

    useBodyScrollLock(expanded);

    useEffect(() => {
        const listener = window.addEventListener(
            "resize",
            setNewDwellerHeight,
            false
        );
        return () => document.removeEventListener("scroll", listener);
    }, []);

    useEffect(() => {
        clearHeight();
        // setNewDwellerHeight();
    }, [expanded]);

    useEffect(() => {
        if (expanded === props.expanded) return;
        if (props.expanded) {
            smoothOnSizeChangeFunc(false, delta);
        } else {
            smoothOnSizeChangeFunc(
                true,
                window.innerHeight * 0.9 - props.bottomOffset - delta
            );
        }
        setExpanded(props.expanded);
    }, [props.expanded, expanded, delta]);

    const clearHeight = () => {
        const dweller = document.getElementById("bottom-dweller");
        if (dweller) {
            dweller.style.transition = `${ANIM_DURATION}ms`;
            dweller.style.removeProperty("height");
        }
    };

    const smoothOnSizeChangeFunc = (down, startDelta) => {
        if (animationRunning) return;
        setAnimationRunning(true);
        if (startDelta && down) {
            startDelta =
                -(window.innerHeight * 0.9 - props.bottomOffset) + startDelta;
        }
        const startValue = Math.abs(
            (startDelta || delta) /
                (window.innerHeight * 0.9 - props.bottomOffset)
        );

        let animStepsDone = 0;
        const interval = setInterval(() => {
            if (animStepsDone > FULL_ANIM_STEPS) {
                clearHeight();
                clearInterval(interval);
                setAnimationRunning(false);
            } else {
                if (animStepsDone > 0) {
                    let outValue =
                        startValue +
                        ((1 - startValue) / FULL_ANIM_STEPS) * animStepsDone;
                    if (down) outValue = 1 - outValue;
                    onSizeChange(
                        Math.max(
                            0,
                            Math.min(100, Math.round(outValue * 10000) / 100)
                        )
                    );
                } else {
                    delta = 0;
                }
                animStepsDone++;
            }
        }, ANIM_DURATION / FULL_ANIM_STEPS);
    };

    const onSizeChange = (value) => {
        props.onSizeChange(value);
        const overlay = document.getElementById("dweller-overlay");
        if (overlay) {
            if (USE_DWELLER_FADE) {
                overlay.style.opacity = `${Math.max(
                    0,
                    Math.min(1, 1 - (value * DWELLER_FADE_MODIFIER) / 100)
                )}`;
            }
            if (USE_DWELLER_SHIFT) {
                const height = overlay.clientHeight;
                overlay.style.bottom = `${
                    -height * ((value * DWELLER_SHIFT_MODIFIER) / 100)
                }px`;
            }
        }
        const backdrop = document.getElementById("dweller-backdrop");
        if (backdrop) {
            backdrop.style.opacity = `${0.5 * (value / 100)}`;
        }
    };

    const expand = () => {
        document.body.style.overflow = "hidden";
        props.onExpand();
    };

    const collapse = () => {
        document.body.style.overflow = "auto";
        props.onCollapse();
    };

    const onClick = useCallback(() => {
        if (expanded) collapse();
        else expand();
    }, [expanded, expand, collapse]);

    const onTouchMove = (e) => {
        const newDelta = touchStart - e.targetTouches[0].clientY;
        delta = newDelta;
        const percentage = Math.abs(
            newDelta /
                (window.innerHeight - (props.bottomOffset + props.topOffset))
        );
        const finalPercentage = expanded ? 1 - percentage : percentage;
        onSizeChange(
            Math.max(
                0,
                Math.min(100, Math.round(finalPercentage * 10000) / 100)
            )
        );
        setNewDwellerHeight();
        e.stopPropagation();
    };

    const onTouchStart = useCallback((e) => {
        delta = 0;
        touchStart = e.targetTouches[0].clientY;
    }, []);

    const onTouchEnd = useCallback(() => {
        if (expanded && delta !== 0) {
            collapse();
        } else if (delta >= props.minDelta) {
            expand();
        } else if (!expanded) {
            if (delta > 0) {
                smoothOnSizeChangeFunc(true, delta);
                delta = 0;
                props.onCollapse();
            } else if (delta < 0) {
                smoothOnSizeChangeFunc(false, delta);
                delta = 0;
            }
        }
    }, [
        expanded,
        delta,
        props.minDelta,
        collapse,
        expand,
        smoothOnSizeChangeFunc,
        props.onCollapse,
    ]);

    useEffect(() => {
        const grabs = document.getElementsByClassName("bottom-dweller-grab");
        const propagationWrapper = (callback, index) => {
            return (ev) => {
                if (lastEventListener !== index) return;
                ev.stopImmediatePropagation();
                callback(ev);
            };
        };
        lastEventListener++;

        for (const grab of grabs) {
            grab.addEventListener(
                "click",
                propagationWrapper(onClick, lastEventListener),
                true
            );
            grab.addEventListener(
                "touchstart",
                propagationWrapper(onTouchStart, lastEventListener),
                true
            );
            grab.addEventListener(
                "touchend",
                propagationWrapper(onTouchEnd, lastEventListener),
                true
            );
            grab.addEventListener(
                "touchmove",
                propagationWrapper(onTouchMove, lastEventListener),
                true
            );
        }
    }, [onClick, onTouchStart, onTouchEnd, onTouchMove]);

    return (
        <>
            <Backdrop id="dweller-backdrop" />
            <BottomDwellerContainer
                style={{ opacity: props.disabled ? 0 : 1 }}
                className={`${expanded ? "expanded" : ""}`}
                topOffset={props.topOffset}
                bottomOffset={props.bottomOffset}
                id="bottom-dweller"
            >
                <GrabMeContainer
                    onClick={onClick}
                    onTouchMove={onTouchMove}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    <GrabMe />
                </GrabMeContainer>
                <ContentContainer
                    onClick={expanded ? () => {} : onClick}
                    onTouchMove={expanded ? () => {} : onTouchMove}
                    onTouchStart={expanded ? () => {} : onTouchStart}
                    onTouchEnd={expanded ? () => {} : onTouchEnd}
                >
                    {props.children}
                    <Overlay
                        id="dweller-overlay"
                        onClick={() => {
                            props.onExpand();
                        }}
                    >
                        {props.overlay}
                    </Overlay>
                </ContentContainer>
            </BottomDwellerContainer>
        </>
    );
};

BottomDweller.defaultProps = {
    onSizeChange: () => {},
    bottomOffset: 100,
    topOffset: 20,
    onExpand: () => {},
    onCollapse: () => {},
    minDelta: 100,
    overlay: null,
};

BottomDweller.propTypes = {
    children: PropTypes.any,
    onSizeChange: PropTypes.func,
    bottomOffset: PropTypes.number,
    topOffset: PropTypes.number,
    expanded: PropTypes.bool,
    onExpand: PropTypes.func,
    onCollapse: PropTypes.func,
    minDelta: PropTypes.number,
    disabled: PropTypes.bool,
    overlay: PropTypes.any,
};

export default BottomDweller;
